import { useState, useEffect, useCallback, useRef } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface ProviderStatus {
  name: string;
  status: "healthy" | "degraded" | "down";
  latencyMs: number;
  errorRate: number;
  lastChecked: string;
  model: string;
}

// ── API helpers ──────────────────────────────────────────────────────────────

const BASE = "/api";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("flax_token") ?? sessionStorage.getItem("flax_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function fetchProviderHealth(): Promise<ProviderStatus[]> {
  const res = await fetch(`${BASE}/admin/providers/health`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch provider health: ${res.status}`);
  const data = await res.json();
  return data.providers;
}

// ── Constants ────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 30_000;

const STATUS_CONFIG = {
  healthy: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    label: "Healthy",
  },
  degraded: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    dot: "bg-amber-500",
    text: "text-amber-700",
    label: "Degraded",
  },
  down: {
    bg: "bg-red-50",
    border: "border-red-200",
    dot: "bg-red-500",
    text: "text-red-700",
    label: "Down",
  },
} as const;

// ── Component ────────────────────────────────────────────────────────────────

export function ProviderHealth() {
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_MS / 1000);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadProviders = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchProviderHealth();
      setProviders(data);
      setLastRefresh(new Date().toLocaleTimeString());
      setCountdown(REFRESH_INTERVAL_MS / 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load provider health");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh
  useEffect(() => {
    loadProviders();

    if (autoRefresh) {
      intervalRef.current = setInterval(loadProviders, REFRESH_INTERVAL_MS);
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [loadProviders, autoRefresh]);

  const handleManualRefresh = () => {
    setLoading(true);
    loadProviders();
  };

  // Summary counts
  const healthyCount = providers.filter((p) => p.status === "healthy").length;
  const degradedCount = providers.filter((p) => p.status === "degraded").length;
  const downCount = providers.filter((p) => p.status === "down").length;

  if (loading && providers.length === 0) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="provider-loading">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
          <p className="mt-3 text-sm text-ink-500">Loading provider health...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="provider-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Provider Health</h1>
          <p className="text-sm text-ink-500">
            LLM provider status and performance
            {lastRefresh && <span className="ml-2">-- Last checked: {lastRefresh}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-ink-300"
              data-testid="auto-refresh-toggle"
            />
            Auto-refresh ({countdown}s)
          </label>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-2 disabled:opacity-50"
          >
            {loading ? "Checking..." : "Check Now"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary Bar */}
      <div className="flex items-center gap-4 rounded-lg border border-ink-100 bg-white p-4" data-testid="status-summary">
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-emerald-500" />
          <span className="text-sm font-medium text-ink-700">{healthyCount} Healthy</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-amber-500" />
          <span className="text-sm font-medium text-ink-700">{degradedCount} Degraded</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-red-500" />
          <span className="text-sm font-medium text-ink-700">{downCount} Down</span>
        </div>
        <div className="ml-auto text-sm text-ink-400">
          {providers.length} provider{providers.length !== 1 ? "s" : ""} monitored
        </div>
      </div>

      {/* Provider Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="provider-grid">
        {providers.map((provider) => (
          <ProviderCard key={provider.name} provider={provider} />
        ))}
        {providers.length === 0 && (
          <p className="col-span-full py-8 text-center text-sm text-ink-400">
            No LLM providers configured. Set API keys in your environment to enable providers.
          </p>
        )}
      </div>

      {/* Latency Comparison */}
      {providers.length > 0 && (
        <div className="rounded-lg border border-ink-100 bg-white p-4" data-testid="latency-comparison">
          <h2 className="mb-3 text-lg font-semibold text-ink-800">Latency Comparison</h2>
          <div className="space-y-2">
            {providers
              .filter((p) => p.latencyMs >= 0)
              .sort((a, b) => a.latencyMs - b.latencyMs)
              .map((provider) => {
                const maxLatency = Math.max(
                  ...providers.filter((p) => p.latencyMs > 0).map((p) => p.latencyMs),
                  1,
                );
                const widthPercent = Math.max(
                  (provider.latencyMs / maxLatency) * 100,
                  2,
                );
                return (
                  <div key={provider.name} className="flex items-center gap-3">
                    <span className="w-20 text-sm font-medium capitalize text-ink-700">
                      {provider.name}
                    </span>
                    <div className="flex-1">
                      <div
                        className={`h-4 rounded ${
                          provider.status === "healthy"
                            ? "bg-emerald-400"
                            : provider.status === "degraded"
                              ? "bg-amber-400"
                              : "bg-red-400"
                        }`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    <span className="w-16 text-right text-sm text-ink-500">
                      {provider.latencyMs}ms
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function ProviderCard({ provider }: { provider: ProviderStatus }) {
  const config = STATUS_CONFIG[provider.status];

  return (
    <div
      className={`rounded-xl border ${config.border} ${config.bg} p-4 transition-shadow hover:shadow-md`}
      data-testid="provider-status-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-3 w-3 rounded-full ${config.dot}`} />
          <h3 className="text-base font-semibold capitalize text-ink-800">
            {provider.name}
          </h3>
        </div>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.text} ${config.bg}`}
        >
          {config.label}
        </span>
      </div>

      {/* Details */}
      <div className="mt-3 space-y-2">
        <DetailRow label="Model" value={provider.model} />
        <DetailRow
          label="Latency"
          value={provider.latencyMs >= 0 ? `${provider.latencyMs}ms` : "N/A"}
        />
        <DetailRow
          label="Error Rate"
          value={`${(provider.errorRate * 100).toFixed(1)}%`}
        />
        <DetailRow
          label="Last Checked"
          value={new Date(provider.lastChecked).toLocaleTimeString()}
        />
      </div>

      {/* Visual latency bar */}
      {provider.latencyMs >= 0 && (
        <div className="mt-3">
          <div className="h-1.5 w-full rounded-full bg-white/60">
            <div
              className={`h-1.5 rounded-full ${
                provider.latencyMs < 500
                  ? "bg-emerald-500"
                  : provider.latencyMs < 2000
                    ? "bg-amber-500"
                    : "bg-red-500"
              }`}
              style={{
                width: `${Math.min((provider.latencyMs / 5000) * 100, 100)}%`,
              }}
            />
          </div>
          <div className="mt-0.5 flex justify-between text-[10px] text-ink-400">
            <span>0ms</span>
            <span>5000ms</span>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-ink-500">{label}</span>
      <span className="font-medium text-ink-700">{value}</span>
    </div>
  );
}
